// src/pages/FaxClaimPage.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { COLORS } from '../constants';
import { Card, LoadingSpinner } from '../components/Common';
import ClaimFormEditor from '../components/ClaimFormEditor';
import customerService from '../services/customerService';
import faxHistoryService from '../services/faxHistoryService';
import consultationService from '../services/consultationService';
import { getClaimFormTemplate } from '../services/claimFormTemplateService';
import { supabase } from '../supabaseClient';
import { readPending, displayStatus, statusPresentation, prepareRequest, historyDetails } from '../utils/faxTracking';

const STORAGE_KEY = 'boplan_fax_claims';
const FAX_API_BASE_URL = (process.env.REACT_APP_FAX_API_BASE_URL || 'https://www.boplan.kr').replace(/\/$/, '');

const CLAIM_TYPES = ['실손', '진단비', '수술비', '입원비', '운전자', '치아', '기타'];

const REQUIRED_DOCS = {
  실손: ['진료비영수증', '진료비세부내역서', '처방전'],
  진단비: ['진료비영수증', '진료비세부내역서', '진단서', '검사결과지'],
  수술비: ['진료비영수증', '진료비세부내역서', '수술확인서', '수술기록지'],
  입원비: ['입퇴원확인서'],
  운전자: ['사고확인서', '진단서 또는 치료확인서'],
  치아: ['치과치료확인서', '치과진료기록', '파노라마 X-ray'],
  기타: ['기타 증빙서류'],
};

const INSURANCE_COMPANIES = [
  { name: 'AIG손보', type: '손해보험', faxType: 'auto', fax: '02-2011-4607', customerCenter: '1544-2792', pdf: '' },
  { name: 'DB손해보험', type: '손해보험', faxType: 'auto', fax: '0505-181-4862', customerCenter: '1588-0100', pdf: '/insurance-forms/DB손해보험금청구서.pdf' },
  { name: 'KB손해보험', type: '손해보험', faxType: 'multi', fax: '', customerCenter: '1544-0114', pdf: '/insurance-forms/KB손해.pdf', faxNumbers: [{ label: '장기', number: '0505-136-6500' }, { label: '일반', number: '055-136-6600' }] },
  { name: 'MG손보', type: '손해보험', faxType: 'auto', fax: '0505-088-1646~9', customerCenter: '1588-5959', pdf: '' },
  { name: '농협손보', type: '손해보험', faxType: 'auto', fax: '0505-060-7000', customerCenter: '1644-9000', pdf: '/insurance-forms/농협손해보험.pdf' },
  { name: '롯데손해보험', type: '손해보험', faxType: 'auto', fax: '0507-333-9999', customerCenter: '1588-3344', pdf: '/insurance-forms/롯데손해.pdf' },
  { name: '메리츠화재', type: '손해보험', faxType: 'multi', fax: '', customerCenter: '1566-7711', pdf: '/insurance-forms/메리츠화재.pdf', faxNumbers: [{ label: '질병', number: '0505-021-3400' }, { label: '상해', number: '0505-021-3500' }] },
  { name: '삼성화재', type: '손해보험', faxType: 'auto', fax: '0505-162-0872', customerCenter: '1588-5114', pdf: '/insurance-forms/삼성화재.pdf' },
  { name: '에이스손보', type: '손해보험', faxType: 'multi', fax: '', customerCenter: '1566-5800', pdf: '', faxNumbers: [{ label: '일반', number: '02-2127-2300' }, { label: '치아', number: '02-6913-8482' }] },
  { name: '한화손해보험', type: '손해보험', faxType: 'auto', fax: '0502-779-1004', customerCenter: '1566-8000', pdf: '/insurance-forms/한화손해.pdf' },
  { name: '하나손해보험', type: '손해보험', faxType: 'auto', fax: '0505-170-0765', customerCenter: '1566-3000', pdf: '' },
  { name: 'AXA손보', type: '손해보험', faxType: 'manual', fax: '', customerCenter: '1566-2266', pdf: '', faxNotice: '콜센터 가상팩스 부여' },
  { name: '현대해상', type: '손해보험', faxType: 'auto', fax: '0507-774-6060', customerCenter: '1588-5656', pdf: '/insurance-forms/현대해상 청구.pdf' },
  { name: '흥국화재', type: '손해보험', faxType: 'auto', fax: '0504-800-0700', customerCenter: '1688-1688', pdf: '/insurance-forms/흥국화재.pdf' },
  { name: '우체국', type: '손해보험', faxType: 'auto', fax: '0505-005-1623', customerCenter: '1588-1300', pdf: '', faxNote: '부산' },
  { name: '신협', type: '손해보험', faxType: 'manual', fax: '', customerCenter: '1544-3030', pdf: '', faxNotice: '해당 계약지점마다 다름' },
  { name: '수협', type: '손해보험', faxType: 'manual', fax: '', customerCenter: '1588-4119', pdf: '', faxNotice: '해당 계약지점마다 다름' },
  { name: '새마을금고', type: '손해보험', faxType: 'manual', fax: '', customerCenter: '1599-9010', pdf: '', faxNotice: '해당 계약지점마다 다름' },
  { name: 'ABL생명', type: '생명보험', faxType: 'auto', fax: '02-3299-5544', customerCenter: '1588-6500', pdf: '/insurance-forms/ABL 사고보험금청구서.pdf' },
  { name: 'AIA생명', type: '생명보험', faxType: 'auto', fax: '02-2021-4540', customerCenter: '1588-9898', pdf: '' },
  { name: 'KB생명', type: '생명보험', faxType: 'auto', fax: '02-6220-9912', customerCenter: '1599-0882', pdf: '' },
  { name: 'KDB생명', type: '생명보험', faxType: 'auto', fax: '02-2669-7930', customerCenter: '1588-4040', pdf: '' },
  { name: '교보생명', type: '생명보험', faxType: 'manual', fax: '', customerCenter: '1588-1001', pdf: '/insurance-forms/교보청구서류.pdf', faxNotice: '콜센터 가상팩스 부여', note: '30만원 이하' },
  { name: '농협생명', type: '생명보험', faxType: 'auto', fax: '02-6971-6040', customerCenter: '1833-4100', pdf: '', note: '200만원 이하' },
  { name: 'DB생명', type: '생명보험', faxType: 'auto', fax: '0505-129-3134', customerCenter: '1588-3131', pdf: '', note: '40만원 이하' },
  { name: '동양생명', type: '생명보험', faxType: 'auto', fax: '02-3289-4517', customerCenter: '1577-1004', pdf: '', note: '정액 30 / 실손 100 이하' },
  { name: '라이나생명', type: '생명보험', faxType: 'multi', fax: '', customerCenter: '1588-0058', pdf: '', faxNumbers: [{ label: '일반', number: '02-6944-1200' }, { label: '치아', number: '02-6944-1283' }] },
  { name: '메트라이프', type: '생명보험', faxType: 'auto', fax: '02-3469-9428', customerCenter: '1588-9600', pdf: '', note: '50만원 이하' },
  { name: '미래에셋생명', type: '생명보험', faxType: 'manual', fax: '', customerCenter: '1588-0220', pdf: '', faxNotice: '콜센터 가상팩스 부여' },
  { name: '삼성생명', type: '생명보험', faxType: 'manual', fax: '', customerCenter: '1588-3114', pdf: '/insurance-forms/삼성생명.pdf', faxNotice: '콜센터 가상팩스 부여', note: '500만원 이하' },
  { name: '신한생명', type: '생명보험', faxType: 'manual', fax: '', customerCenter: '1588-5580', pdf: '/insurance-forms/신한생명.pdf', faxNotice: '콜센터 가상팩스 부여' },
  { name: '처브라이프', type: '생명보험', faxType: 'auto', fax: '02-3480-7801', customerCenter: '1599-4600', pdf: '' },
  { name: '푸본현대생명', type: '생명보험', faxType: 'auto', fax: '0505-106-0311', customerCenter: '1577-3311', pdf: '' },
  { name: '한화생명', type: '생명보험', faxType: 'manual', fax: '', customerCenter: '1588-6363', pdf: '/insurance-forms/한화생명.pdf', faxNotice: '콜센터 가상팩스 부여', note: '100만원 이하' },
  { name: '흥국생명', type: '생명보험', faxType: 'manual', fax: '', customerCenter: '1588-2288', pdf: '/insurance-forms/흥국생명.pdf', faxNotice: '콜센터 가상팩스 부여' },
  { name: '신한라이프', type: '생명보험', faxType: 'manual', fax: '', customerCenter: '1588-5580', pdf: '', faxNotice: '콜센터 가상팩스 부여' },
  { name: '푸르덴셜생명', type: '생명보험', faxType: 'manual', fax: '', customerCenter: '1588-3374', pdf: '', faxNotice: '해당 계약지점마다 다름' },
  { name: 'DGB생명', type: '생명보험', faxType: 'auto', fax: '0505-083-5420', customerCenter: '1588-4770', pdf: '' },
  { name: '하나생명', type: '생명보험', faxType: 'manual', fax: '', customerCenter: '1577-1112', pdf: '' },
];

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
}

function fileSizeText(size) {
  if (!size) return '';
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)}KB`;
  return `${(size / 1024 / 1024).toFixed(1)}MB`;
}

function getFaxLabel(company) {
  if (!company) return '-';
  if (company.faxNumbers?.length) {
    return company.faxNumbers.map((item) => `${item.label} ${item.number}`).join(' / ');
  }
  if (company.faxNotice) return company.faxNotice;
  if (company.fax) return company.faxNote ? `${company.faxNote} ${company.fax}` : company.fax;
  return '직접 입력 필요';
}

function needsManualFax(company) {
  return company?.faxType === 'manual' || company?.faxType === 'multi';
}

function countPdfPagesFromText(text) {
  const pageObjects = text.match(/\/Type\s*\/Page(?!s)\b/g)?.length || 0;
  const pageTreeCounts = Array.from(text.matchAll(/\/Count\s+(\d+)/g), (match) => Number(match[1]));
  return Math.max(1, pageObjects, ...pageTreeCounts.filter(Number.isFinite));
}

async function countFilePages(file) {
  if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) return 1;

  try {
    const buffer = await file.arrayBuffer();
    const text = new TextDecoder('latin1').decode(buffer);
    return countPdfPagesFromText(text);
  } catch (error) {
    return 1;
  }
}

export default function FaxClaimPage({ onBack, profile, setProfile }) {
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [faxNumber, setFaxNumber] = useState('');
  const [claimType, setClaimType] = useState('실손');
  const [memo, setMemo] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePageCounts, setFilePageCounts] = useState([]);
  const [isCountingPages, setIsCountingPages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [claimEditorOpen, setClaimEditorOpen] = useState(false);
  const fileSelectionIdRef = useRef(0);
  const sendLockRef = useRef(false);
  const submittedDraftRef = useRef(false);
  const draftRequestRef = useRef(undefined);
  const draftUserRef = useRef(null);
  const [faxStatus, setFaxStatus] = useState({ status: 'loading' });
  const [retryRequest, setRetryRequest] = useState(null);
  const faxPresentation = statusPresentation(faxStatus);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  const selectedCompanyInfo = INSURANCE_COMPANIES.find((item) => item.name === selectedCompany);
  const selectedClaimFormTemplate = getClaimFormTemplate(selectedCompanyInfo?.name);
  const totalFaxPages = useMemo(
    () => filePageCounts.reduce((sum, count) => sum + count, 0),
    [filePageCounts]
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim();

    if (!q) {
      return isMobile ? [] : customers.slice(0, 20);
    }

    return customers
      .filter((c) => {
        const name = c.name || '';
        const phone = c.phone || '';
        return name.includes(q) || phone.includes(q);
      })
      .slice(0, 20);
  }, [customers, customerSearch, isMobile]);

  const requiredDocs = REQUIRED_DOCS[claimType] || REQUIRED_DOCS.기타;
  const showCustomerList = !isMobile || customerSearch.trim().length > 0;

  useEffect(() => {
    loadCustomers();
    loadClaims();
    refreshPending();
    const onFocus = () => { if (!sendLockRef.current) refreshPending(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  async function loadCustomers() {
    setLoading(true);
    try {
      const data = await customerService.list({ status: '전체', search: '' });
      setCustomers(data || []);
    } catch (e) {
      alert('고객 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }

  async function loadClaims() {
  try {
    const data = await faxHistoryService.list();
    setClaims(data || []);
  } catch (e) {
    alert('팩스 이력을 불러오지 못했습니다.');
  }
}

  function handleCompanyChange(name) {
    setSelectedCompany(name);
    const found = INSURANCE_COMPANIES.find((item) => item.name === name);

    if (!found) {
      setFaxNumber('');
      return;
    }

    setFaxNumber(found.faxType === 'auto' ? found.fax || '' : '');
  }

 async function handleFileChange(e) {
  if (sendLockRef.current) return;
  submittedDraftRef.current = false;
  const files = Array.from(e.target.files || []);
  const selectionId = fileSelectionIdRef.current + 1;
  fileSelectionIdRef.current = selectionId;
  setSelectedFiles(files);
  setFilePageCounts([]);
  setIsCountingPages(true);

  try {
    const counts = await Promise.all(files.map(countFilePages));
    if (fileSelectionIdRef.current === selectionId) setFilePageCounts(counts);
  } finally {
    if (fileSelectionIdRef.current === selectionId) setIsCountingPages(false);
  }
}

  async function copyFaxNumber() {
    if (!faxNumber) {
      alert('복사할 팩스번호가 없습니다.');
      return;
    }
    try {
      await navigator.clipboard.writeText(faxNumber);
      alert('팩스번호가 복사되었습니다.');
    } catch {
      alert(`팩스번호: ${faxNumber}`);
    }
  }

  function resetForm() {
    setSelectedCustomer(null);
    setCustomerSearch('');
    setSelectedCompany('');
    setFaxNumber('');
    setClaimType('실손');
    setMemo('');
    setSelectedFiles([]);
    setFilePageCounts([]);
  }

 async function withFaxLock(action) {
   if (sendLockRef.current) return;
   sendLockRef.current = true;
   setIsSending(true);
   try {
     if (!navigator.locks) throw new Error('LOCK_UNAVAILABLE');
     await navigator.locks.request('boplan-fax-send', { ifAvailable: true }, async (lock) => {
       if (!lock) { alert('다른 창에서 팩스 요청을 처리 중입니다.'); return; }
       await action();
     });
   } catch {
     setFaxStatus({ status: 'lookup_failed' });
     alert('요청 상태를 확인하지 못했습니다. 중복 발송 방지를 위해 다시 발송하지 말고 청구이력을 확인해주세요.');
   } finally {
     sendLockRef.current = false;
     setIsSending(false);
   }
 }

 async function lookupPending(session, requestId) {
   const response = await fetch(`${FAX_API_BASE_URL}/api/send-fax`, {
     method: 'POST', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
     body: JSON.stringify({ requestId, statusOnly: true }),
   });
   if (!response.ok) throw new Error('STATUS_UNAVAILABLE');
   return response.json();
 }

 async function refreshPending(rewrite = false, locked = false) {
   try {
   const { data: { session } } = await supabase.auth.getSession();
   if (!session?.user?.id) throw new Error('AUTH_REQUIRED');
   const requestId = readPending(window.localStorage, session.user.id);
   const result = requestId ? await lookupPending(session, requestId) : { status: 'idle' };
   if (sendLockRef.current && !locked) return;
   if (draftRequestRef.current === undefined || draftUserRef.current !== session.user.id) {
     if (draftUserRef.current && draftUserRef.current !== session.user.id) { resetForm(); setRetryRequest(null); }
     draftRequestRef.current = requestId;
     draftUserRef.current = session.user.id;
   } else if (draftRequestRef.current !== requestId) {
     // Another tab changed the attempt. Discard the stale form before adopting it.
     resetForm();
     setRetryRequest(null);
     draftRequestRef.current = requestId;
   }
   setFaxStatus(result);
   if (rewrite) {
     if (result.status !== 'failed' || result.accounting_status !== 'refunded') return;
     if (!window.confirm('환불이 완료된 실패 건입니다. 별도의 새 발송으로 다시 작성할까요?')) return;
     resetForm();
     setRetryRequest(requestId);
   }
   } catch { setFaxStatus({ status: 'lookup_failed' }); }
 }

 async function handleSendFax() {
  if (submittedDraftRef.current) return;
  if (faxPresentation.blocked && !(faxPresentation.retry && retryRequest && retryRequest === draftRequestRef.current)) return;
  if (isCountingPages) {
    alert('첨부파일 장수를 계산하고 있습니다. 잠시 후 다시 시도해주세요.');
    return;
  }


  const estimatedPages = Math.max(1, totalFaxPages);

  if ((profile?.fax_credit ?? 0) < estimatedPages) {
    alert('팩스 크레딧이 부족합니다.');
    return;
  }

  if (!selectedCustomer) {
    alert('고객을 선택해주세요.');
    return;
  }

  if (!selectedCompany) {
    alert('보험사를 선택해주세요.');
    return;
  }
  if (!faxNumber) {
    alert('팩스번호를 입력하거나 보험사를 선택해주세요.');
    return;
  }
  if (selectedFiles.length === 0) {
    alert('팩스로 발송할 첨부파일을 선택해주세요.');
    return;
  }

  if (!window.confirm(`${selectedCompany}로 팩스를 발송할까요?\n예상 차감: ${estimatedPages}장`)) return;

  setIsSending(true);

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.access_token) throw new Error('로그인이 만료되었습니다. 다시 로그인해주세요.');

    if (draftUserRef.current !== session.user.id) throw new Error('STALE_DRAFT');
    const requestId = await prepareRequest({
      storage: window.localStorage, userId: session.user.id,
      expectedRequest: draftRequestRef.current, retryRequest,
      lookup: async (id) => {
        const result = await lookupPending(session, id);
        setFaxStatus(result);
        return result;
      },
      randomUUID: () => window.crypto.randomUUID(),
    });
    draftRequestRef.current = requestId;
    submittedDraftRef.current = true;
    setRetryRequest(null);
    setFaxStatus({ status: 'processing' });

    const uploadedFiles = [];
    try {
      for (const file of selectedFiles) {
        const signedResponse = await fetch(`${FAX_API_BASE_URL}/api/fax-upload-url`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: file.name, size: file.size, type: file.type }),
        });
        const signedContentType = signedResponse.headers.get('content-type') || '';
        if (!signedContentType.includes('application/json')) {
          throw new Error('팩스 파일 업로드 서버가 올바른 응답을 반환하지 않았습니다.');
        }
        const signedResult = await signedResponse.json();

        if (!signedResponse.ok || !signedResult.success) {
          throw new Error(signedResult.error || '팩스 파일 업로드 준비에 실패했습니다.');
        }

        const { error: uploadError } = await supabase.storage
          .from(signedResult.bucket)
          .uploadToSignedUrl(signedResult.path, signedResult.token, file, {
            contentType: file.type,
          });

        if (uploadError) throw uploadError;
        uploadedFiles.push({
          bucket: signedResult.bucket,
          path: signedResult.path,
          name: file.name,
          size: file.size,
          type: file.type,
        });
      }
    } catch (uploadError) {
      if (uploadedFiles.length > 0) {
        await fetch(`${FAX_API_BASE_URL}/api/fax-upload-url`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ paths: uploadedFiles.map((file) => file.path) }),
        }).catch(() => {});
      }
      throw new Error('UPLOAD_FAILED');
    }

    const response = await fetch(`${FAX_API_BASE_URL}/api/send-fax`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        receiverNum: faxNumber,
        receiverName: selectedCompany,
        title: `${selectedCustomer.name || '고객'} ${claimType} 보험금 청구`,
        customerId: String(selectedCustomer.db_id || selectedCustomer.id || ''),
        insuranceCompany: selectedCompany,
        requestId,
        files: uploadedFiles,
      }),
    });

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      throw new Error(
        response.status === 401
          ? '팩스 서버 접근이 차단되었습니다. 실사용 도메인에서 다시 시도해주세요.'
          : '팩스 서버가 올바른 응답을 반환하지 않았습니다. 잠시 후 다시 시도해주세요.'
      );
    }

    const result = await response.json();
    setFaxStatus(result.status ? result : { status: 'lookup_failed' });

    if (!response.ok || !result.success) {
      if (Number.isFinite(Number(result.remaining_credit))) {
        setProfile((prev) => ({ ...prev, fax_credit: Number(result.remaining_credit) }));
      }
      alert(result.status ? displayStatus({ status: result.status }, result.accounting_status) : '전송 상태를 확인하지 못했습니다. 다시 발송하지 마세요.');
      await loadClaims();
      return;
    }

    const chargedPages = Number(result.total_pages);
    const serverPageCounts = result.page_counts;
    const remainingCredit = Number(result.remaining_credit);

    if (!Number.isInteger(chargedPages) || chargedPages <= 0 || !Array.isArray(serverPageCounts)) {
      throw new Error('서버의 팩스 장수 응답이 올바르지 않습니다.');
    }

    if (!Number.isInteger(remainingCredit) || remainingCredit < 0) {
      throw new Error('서버의 잔여 팩스 응답이 올바르지 않습니다.');
    }

    const now = new Date().toISOString();

    setProfile((prev) => ({
      ...prev,
      fax_credit: remainingCredit,
    }));

    const item = {
      id: `fax-${Date.now()}`,
      customer_id: String(selectedCustomer.db_id || selectedCustomer.id || ''),
      customer_name: selectedCustomer.name || '',
      customer_phone: selectedCustomer.phone || '',
      insurance_company: selectedCompany,
      fax_number: faxNumber,
      claim_type: claimType,
      memo: memo.trim(),
      files: selectedFiles.map((file, index) => ({
        name: file.name,
        size: file.size,
        type: file.type,
        page_count: serverPageCounts[index],
      })),
      status: '발송완료',
      provider: 'popbill',
      provider_receipt_id: result.receiptNum,
      sent_at: now,
      receipt_num: result.receiptNum,
      request_num: result.requestNum,
      created_at: now,
      updated_at: now,
    };

    let historySaved = true;
    try {
      if (!result.duplicate) await consultationService.create({
        customer_id: item.customer_id,
        customer_name: item.customer_name,
        category: '청구',
        content: `[보험금청구]
보험사: ${selectedCompany}
청구유형: ${claimType}
팩스번호: ${faxNumber}
접수번호: ${result.receiptNum}
메모: ${memo.trim() || '-'}`,
        consulted_at: now,
      });
      await loadClaims();
    } catch (historyError) {
      historySaved = false;
    }

    alert(
      `팩스 접수 완료!\n접수번호: ${result.receiptNum}\n차감: ${chargedPages}장\nPopbill 접수 성공이며 최종 수신 완료를 의미하지 않습니다.` +
      (historySaved ? '' : '\n단, 발송 이력 저장을 확인해주세요.')
    );
    resetForm();
  } catch (e) {
    setFaxStatus({ status: 'lookup_failed' });
    alert('전송 상태를 확인하지 못했습니다. 상태 다시 확인을 이용하고 다시 발송하지 마세요.');
  } finally {
    setIsSending(false);
  }
}

  async function handleDeleteClaim(id) {
  if (!window.confirm('이 청구이력을 삭제할까요?')) return;

  try {
    await faxHistoryService.remove(id);
    await loadClaims();
  } catch (e) {
    alert('청구이력 삭제 실패');
  }
}

  function openClaimForm() {
    if (!selectedCompanyInfo?.pdf) {
      alert('등록된 청구서 양식이 없습니다.');
      return;
    }
    window.open(selectedCompanyInfo.pdf, '_blank');
  }

  function openClaimEditor() {
    if (!selectedClaimFormTemplate) {
      alert('현재 이 보험사의 청구서 자동작성은 지원하지 않습니다.');
      return;
    }

    if (!selectedCustomer) {
      alert('고객을 먼저 선택해주세요.');
      return;
    }

    setClaimEditorOpen(true);
  }

  async function addGeneratedClaimFormFile(file) {
    if (!file || sendLockRef.current) return;
    submittedDraftRef.current = false;

    setSelectedFiles((prev) => [...prev, file]);
    setIsCountingPages(true);

    try {
      const pageCount = await countFilePages(file);
      setFilePageCounts((prev) => [...prev, pageCount]);
      alert('작성된 청구서가 팩스 첨부파일에 추가되었습니다.');
    } catch (error) {
      alert('작성된 청구서를 첨부하지 못했습니다.');
    } finally {
      setIsCountingPages(false);
    }
  }

  function renderCustomerCard() {
    return (
      <Card>
        <SectionTitle icon="👤" title="고객 선택" />
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <input
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="고객명 또는 전화번호 검색"
              style={styles.input}
            />

            {selectedCustomer && (
              <div style={styles.selectedBox}>
                <div style={{ fontWeight: 900, color: COLORS.primary }}>선택된 고객</div>
                <div style={{ marginTop: 5, fontSize: 14, color: COLORS.text }}>
                  {selectedCustomer.name} · {selectedCustomer.phone || '-'}
                </div>
              </div>
            )}

            {!showCustomerList ? (
              <div style={styles.mobileSearchGuide}>고객명을 검색하면 목록이 표시됩니다.</div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  marginTop: 10,
                  maxHeight: isMobile ? 220 : 300,
                  overflowY: 'auto',
                }}
              >
                {filteredCustomers.length === 0 ? (
                  <div style={styles.emptyText}>검색된 고객이 없습니다.</div>
                ) : (
                  filteredCustomers.map((customer) => {
                    const id = customer.db_id || customer.app_customer_id || customer.id;
                    const active =
                      String(selectedCustomer?.db_id || selectedCustomer?.app_customer_id || selectedCustomer?.id || '') ===
                      String(id);

                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setSelectedCustomer(customer)}
                        style={{
                          ...styles.customerButton,
                          borderColor: active ? COLORS.primary : COLORS.border,
                          background: active ? COLORS.primaryBg : '#fff',
                        }}
                      >
                        <div style={{ fontWeight: 900, color: COLORS.text }}>{customer.name}</div>
                        <div style={{ fontSize: 12, color: COLORS.textGray, marginTop: 3 }}>
                          {customer.phone || '-'}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}
      </Card>
    );
  }

  function renderInsuranceCard() {
    return (
      <Card>
        <SectionTitle icon="🏢" title="보험사 / 팩스번호" />
        <select value={selectedCompany} onChange={(e) => handleCompanyChange(e.target.value)} style={styles.input}>
          <option value="">보험사 선택</option>
          {INSURANCE_COMPANIES.map((company) => (
            <option key={company.name} value={company.name}>
              {company.name}
            </option>
          ))}
        </select>

        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <input
            value={faxNumber}
            onChange={(e) => setFaxNumber(e.target.value)}
            placeholder={needsManualFax(selectedCompanyInfo) ? '안내받은 팩스번호 직접 입력' : '팩스번호'}
            style={{ ...styles.input, marginTop: 0, flex: 1, minWidth: 0 }}
          />
          <button type="button" onClick={copyFaxNumber} style={styles.copyButton}>
            복사
          </button>
        </div>

        {selectedCompanyInfo && (
          <div style={styles.companyInfoBox}>
            <div style={styles.companyInfoRow}>
              <span>고객센터</span>
              <strong>{selectedCompanyInfo.customerCenter || '-'}</strong>
            </div>
            <div style={styles.companyInfoRow}>
              <span>팩스</span>
              <strong>{getFaxLabel(selectedCompanyInfo)}</strong>
            </div>
            {selectedCompanyInfo.note && <div style={styles.companyNote}>※ {selectedCompanyInfo.note}</div>}
            {selectedCompanyInfo.faxNumbers?.length > 0 && (
              <div style={styles.companyNote}>※ 용도에 맞는 팩스번호를 확인한 뒤 직접 입력해주세요.</div>
            )}
          </div>
        )}

        <div style={styles.formButtonRow}>
          <button type="button" onClick={openClaimForm} style={styles.formButton}>
            📄 청구서 보기
          </button>
          {selectedClaimFormTemplate && (
            <button type="button" onClick={openClaimEditor} style={styles.writeFormButton}>
              ✍️ 청구서 작성
            </button>
          )}
        </div>

        {needsManualFax(selectedCompanyInfo) && (
          <div style={styles.manualNotice}>
            📢 해당 보험사는 보험금 청구용 팩스번호를 확인 후 이용해주세요.
            <br />
            고객센터 : {selectedCompanyInfo.customerCenter}
            <br />
            {selectedCompanyInfo.faxNotice || '용도에 맞는 팩스번호를'} 직접 입력해주세요.
          </div>
        )}

        <div style={styles.helpText}>※ 보험사별 청구 기준과 팩스번호는 접수 전 한 번 더 확인해주세요.</div>
      </Card>
    );
  }

  function renderClaimTypeCard() {
    return (
      <Card>
        <SectionTitle icon="📄" title="청구유형 / 필요서류" />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {CLAIM_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setClaimType(type)}
              style={{
                ...styles.chipButton,
                background: claimType === type ? COLORS.primary : COLORS.primaryBg,
                color: claimType === type ? '#fff' : COLORS.primary,
              }}
            >
              {type}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {requiredDocs.map((doc) => (
            <label key={doc} style={styles.checkRow}>
              <input type="checkbox" />
              <span>{doc}</span>
            </label>
          ))}
        </div>
      </Card>
    );
  }

  function renderFileCard() {
    return (
      <Card>
        <SectionTitle icon="📎" title="첨부파일" />
        <input
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          onChange={handleFileChange}
          style={styles.fileInput}
        />

        <div style={styles.helpText}>PDF는 문서 페이지 수, JPG와 PNG는 파일당 1장으로 계산됩니다.</div>

        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {selectedFiles.length === 0 ? (
            <div style={styles.emptyText}>선택된 파일이 없습니다.</div>
          ) : (
            selectedFiles.map((file, idx) => (
              <div key={`${file.name}-${idx}`} style={styles.fileItem}>
                <span>📎 {file.name}</span>
                <span style={{ color: COLORS.textGray }}>
                  {fileSizeText(file.size)} · {filePageCounts[idx] || 1}장
                </span>
              </div>
            ))
          )}
        </div>

        {selectedFiles.length > 0 && (
          <div style={{ ...styles.helpText, marginTop: 12, fontWeight: 800, color: COLORS.primary }}>
            예상 차감 {totalFaxPages}장 · 잔여 팩스 {profile?.fax_credit ?? 0}장
          </div>
        )}
      </Card>
    );
  }

  function renderMemoCard() {
    return (
      <Card>
        <SectionTitle icon="📝" title="청구 메모" />
        <textarea
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="청구 메모를 입력하세요. 예: 6/5 고객 서류 수령, 메리츠 실손청구용"
          rows={isMobile ? 5 : 8}
          style={styles.textarea}
        />

        <button
          type="button"
          onClick={() => withFaxLock(handleSendFax)}
          disabled={isSending || isCountingPages || (faxPresentation.blocked && !(faxPresentation.retry && retryRequest && retryRequest === draftRequestRef.current))}
          style={{ ...styles.primaryButton, opacity: isSending || isCountingPages || (faxPresentation.blocked && !retryRequest) ? 0.6 : 1 }}
        >
  {isSending ? '발송 중...' : isCountingPages ? '장수 계산 중...' : '📠 팩스 발송'}
</button>

        {faxPresentation.message && <div role="status" style={styles.helpText}>{faxPresentation.message}</div>}
        {faxPresentation.refresh && <button type="button" disabled={isSending} onClick={() => withFaxLock(async () => { await refreshPending(false, true); await loadClaims(); })}>상태 다시 확인</button>}
        {faxPresentation.retry && !retryRequest && <button type="button" disabled={isSending} onClick={() => withFaxLock(() => refreshPending(true, true))}>다시 작성</button>}
        <div style={styles.helpText}>※ 발송 시도부터 이력이 저장됩니다. 결과 확인 중에는 다시 발송하지 마세요.</div>
      </Card>
    );
  }

  function renderHistoryCard() {
    return (
      <Card>
        <SectionTitle icon="🗂️" title="청구이력" />
        {claims.length === 0 ? (
          <div style={styles.emptyLarge}>아직 저장된 청구이력이 없습니다.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {claims.map((claim) => (
              <div key={claim.id} style={styles.claimCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 900, color: COLORS.text }}>📠 {claim.customer_name || '고객명 없음'}</div>
                    <div style={{ fontSize: 12, color: COLORS.textGray, marginTop: 3 }}>{claim.customer_phone || '-'}</div>
                  </div>
                  <span style={styles.claimTypeBadge}>{claim.claim_type}</span>
                </div>

                <div style={styles.claimGrid}>
                  <Info label="상태" value={claim.status === 'failed' ? statusPresentation({ status: claim.status, accounting_status: claim.credit_status }).message : displayStatus(claim, claim.credit_status)} />
                  <Info label="보험사" value={claim.insurance_company} />
                  <Info label="청구유형" value={claim.claim_type} />
                  <Info label="팩스번호" value={claim.fax_number} />
                  <Info label="발송일시" value={historyDetails(claim).sentAt} />
                  <Info label="총 장수" value={historyDetails(claim).pages} />
                  {claim.status !== 'sent' && <Info label="요청일시" value={formatDateTime(claim.created_at)} />}
                  {(claim.status === 'sent' || claim.status === '발송완료') && claim.provider_receipt_id && (
                    <Info label="접수번호" value={claim.provider_receipt_id} />
                  )}
                </div>

                {claim.status === 'sent' && <div style={styles.helpText}>Popbill 접수 성공 기준이며 최종 수신 완료를 의미하지 않습니다.</div>}

                {claim.memo && <div style={styles.memoBox}>📝 {claim.memo}</div>}

                {claim.files?.length > 0 && (
                  <div style={styles.fileListBox}>
                    {claim.files.map((file, idx) => (
                      <span key={`${claim.id}-${file.name}-${idx}`} style={styles.fileBadge}>
                        📎 {file.name}{file.page_count ? ` · ${file.page_count}장` : ''}
                      </span>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                  {!claim.request_id && <button type="button" onClick={() => handleDeleteClaim(claim.id)} style={styles.deleteButton}>
                    삭제
                  </button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    );
  }

  return (
    <div style={{ minHeight: '100%', background: COLORS.bg, padding: isMobile ? 12 : 16, boxSizing: 'border-box', overflowY: 'auto' }}>
      <div
        style={{
          maxWidth: 1080,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          paddingBottom: 40,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: isMobile ? 22 : 22, fontWeight: 900, color: COLORS.text }}>📠 보험팩스청구</div>
            <div style={{ fontSize: 13, color: COLORS.textGray, marginTop: 4 }}>고객별 보험금 청구이력을 저장하고 관리합니다.</div>
          </div>

          {onBack && (
            <button type="button" onClick={onBack} style={styles.ghostButton}>
              ← 홈
            </button>
          )}
        </div>

        <Card>
          <div style={{ background: 'linear-gradient(135deg,#7C3AED,#A78BFA)', color: '#fff', borderRadius: 16, padding: isMobile ? 14 : 16 }}>
           <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 6 }}>
  📠 보험금 팩스청구
</div>
<div style={{ fontSize: 13, lineHeight: 1.6, opacity: 0.95 }}>
  고객 선택, 보험사 선택, 청구서류 첨부 후 팩스를 발송할 수 있습니다.
  발송이 완료되면 청구이력이 자동으로 저장됩니다.
  <br />
  ※ 발송 전 보험사 팩스번호와 첨부서류를 꼭 확인해주세요.
</div>
          </div>
        </Card>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {renderCustomerCard()}
            {renderInsuranceCard()}
            {renderClaimTypeCard()}
            {renderFileCard()}
            {renderMemoCard()}
            {renderHistoryCard()}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.52fr) minmax(0, 0.48fr)', gap: 14, alignItems: 'start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {renderCustomerCard()}
              {renderHistoryCard()}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {renderInsuranceCard()}
              {renderClaimTypeCard()}
              {renderFileCard()}
              {renderMemoCard()}
            </div>
          </div>
        )}
      </div>
      <ClaimFormEditor
        visible={claimEditorOpen}
        onClose={() => setClaimEditorOpen(false)}
        customer={selectedCustomer}
        company={selectedCompanyInfo}
        onAddFile={addGeneratedClaimFormFile}
      />
    </div>
  );
}

function SectionTitle({ icon, title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      <span style={{ fontSize: 16, fontWeight: 900, color: COLORS.text }}>{title}</span>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: COLORS.textGray }}>{label}</div>
      <div style={{ fontSize: 13, color: COLORS.text, fontWeight: 800, marginTop: 3 }}>{value || '-'}</div>
    </div>
  );
}

const styles = {
  input: {
    width: '100%',
    border: `1.5px solid ${COLORS.border}`,
    background: '#fff',
    borderRadius: 12,
    padding: '12px 14px',
    fontSize: 14,
    outline: 'none',
    color: COLORS.text,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    border: `1.5px solid ${COLORS.border}`,
    background: '#fff',
    borderRadius: 12,
    padding: '12px 14px',
    fontSize: 14,
    outline: 'none',
    color: COLORS.text,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  fileInput: {
    width: '100%',
    border: `1.5px dashed ${COLORS.border}`,
    background: '#FAFAFF',
    borderRadius: 12,
    padding: 14,
    boxSizing: 'border-box',
    fontSize: 13,
    color: COLORS.text,
  },
  selectedBox: {
    background: COLORS.primaryBg,
    borderRadius: 12,
    padding: 12,
    marginTop: 10,
  },
  mobileSearchGuide: {
    marginTop: 10,
    background: '#F8FAFC',
    borderRadius: 12,
    padding: 12,
    fontSize: 13,
    color: COLORS.textGray,
    textAlign: 'center',
  },
  customerButton: {
    width: '100%',
    textAlign: 'left',
    border: `1.5px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 12,
    cursor: 'pointer',
  },
  copyButton: {
    border: 'none',
    background: COLORS.primary,
    color: '#fff',
    borderRadius: 12,
    padding: '0 16px',
    fontSize: 13,
    fontWeight: 900,
    cursor: 'pointer',
    flexShrink: 0,
  },
  formButtonRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 8,
    marginTop: 10,
  },
  formButton: {
    width: '100%',
    border: 'none',
    background: '#EEF2FF',
    color: COLORS.primary,
    borderRadius: 12,
    padding: '12px 0',
    fontWeight: 900,
    cursor: 'pointer',
  },
  writeFormButton: {
    width: '100%',
    border: 'none',
    background: COLORS.primary,
    color: '#fff',
    borderRadius: 12,
    padding: '12px 0',
    fontWeight: 900,
    cursor: 'pointer',
  },
  manualNotice: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    background: '#FEF3C7',
    border: '1px solid #F59E0B',
    color: '#92400E',
    fontSize: 13,
    lineHeight: 1.6,
  },
  companyInfoBox: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    background: '#F8FAFC',
    border: `1px solid ${COLORS.border}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  companyInfoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    fontSize: 13,
    color: COLORS.textGray,
    lineHeight: 1.45,
  },
  companyNote: {
    fontSize: 12,
    color: COLORS.textGray,
    lineHeight: 1.5,
  },
  chipButton: {
    border: 'none',
    borderRadius: 999,
    padding: '8px 13px',
    fontSize: 13,
    fontWeight: 900,
    cursor: 'pointer',
  },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#F8FAFC',
    borderRadius: 10,
    padding: '9px 11px',
    fontSize: 13,
    color: COLORS.text,
    fontWeight: 700,
  },
  primaryButton: {
    width: '100%',
    marginTop: 14,
    border: 'none',
    background: COLORS.primary,
    color: '#fff',
    borderRadius: 14,
    padding: '14px 0',
    fontSize: 15,
    fontWeight: 900,
    cursor: 'pointer',
  },
  ghostButton: {
    border: `1px solid ${COLORS.border}`,
    background: '#fff',
    color: COLORS.text,
    borderRadius: 999,
    padding: '8px 13px',
    fontSize: 13,
    fontWeight: 900,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  deleteButton: {
    border: 'none',
    background: '#FEE2E2',
    color: '#DC2626',
    borderRadius: 999,
    padding: '6px 11px',
    fontSize: 12,
    fontWeight: 900,
    cursor: 'pointer',
  },
  helpText: {
    fontSize: 12,
    color: COLORS.textGray,
    marginTop: 8,
    lineHeight: 1.5,
  },
  emptyText: {
    fontSize: 13,
    color: COLORS.textGray,
    padding: '8px 0',
  },
  emptyLarge: {
    background: '#F8FAFC',
    color: COLORS.textGray,
    borderRadius: 14,
    padding: 24,
    textAlign: 'center',
    fontSize: 14,
  },
  fileItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    background: '#F8FAFC',
    borderRadius: 10,
    padding: '9px 11px',
    fontSize: 12,
    color: COLORS.text,
  },
  claimCard: {
    border: `1px solid ${COLORS.border}`,
    background: '#fff',
    borderRadius: 16,
    padding: 14,
  },
  claimGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
    marginTop: 12,
  },
  memoBox: {
    background: COLORS.primaryBg,
    color: COLORS.text,
    borderRadius: 12,
    padding: 10,
    fontSize: 13,
    lineHeight: 1.5,
    marginTop: 12,
  },
  fileListBox: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginTop: 10,
  },
  fileBadge: {
    background: '#F3F4F6',
    color: COLORS.textGray,
    borderRadius: 999,
    padding: '5px 9px',
    fontSize: 11,
    fontWeight: 800,
  },
  claimTypeBadge: {
    background: COLORS.primaryBg,
    color: COLORS.primary,
    borderRadius: 999,
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 900,
    alignSelf: 'flex-start',
    flexShrink: 0,
  },
};
